import type { ScopeTarget } from "@/components/features/FormBuilder/utils/scopeAssignments";

const ALL = "all";
const KEY_SEPARATOR = "|";
const LABEL_SEPARATOR = " › ";

export interface NamedItem {
  id: string;
  label: string;
}

export interface ScopeOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface VisiblePickers {
  entities: boolean;
  clients: boolean;
  brands: boolean;
}

export interface ScopeSelection {
  entityIds: string[];
  clientKeys: string[];
  brandKeys: string[];
}

export interface ScopeSummaryRow {
  key: string;
  label: string;
  target: ScopeTarget;
}

export interface ScopeLabels {
  entities: Record<string, string>;
  clients: Record<string, string>;
  brands: Record<string, string>;
}

const encodeKey = (parentId: string, childId: string) => `${parentId}${KEY_SEPARATOR}${childId}`;

const decodeKey = (key: string) => {
  const [parentId, childId] = key.split(KEY_SEPARATOR);
  return { parentId, childId };
};

export const encodeAllKey = (parentId: string) => encodeKey(parentId, ALL);
export const encodeChildKey = encodeKey;

const joinLabel = (...parts: string[]) => parts.join(LABEL_SEPARATOR);

export function getVisiblePickers(levelLabels: string[]): VisiblePickers {
  const levels = new Set(levelLabels.map((label) => label.trim().toLowerCase()));
  const brands = levels.has("brand");
  const clients = brands || levels.has("client");
  return { entities: clients || levels.has("entity"), clients, brands };
}

export function pruneSelection(
  { entityIds, clientKeys, brandKeys }: ScopeSelection,
  pickers: VisiblePickers,
): ScopeSelection {
  if (!pickers.entities) return { entityIds: [], clientKeys: [], brandKeys: [] };

  const entitySet = new Set(entityIds);
  const allClientsEntities = new Set(
    clientKeys
      .filter((key) => decodeKey(key).childId === ALL)
      .map((key) => decodeKey(key).parentId),
  );
  const keptClientKeys = pickers.clients
    ? clientKeys.filter((key) => {
        const { parentId, childId } = decodeKey(key);
        if (!entitySet.has(parentId)) return false;
        return childId === ALL || !allClientsEntities.has(parentId);
      })
    : [];

  const specificClientIds = new Set(
    keptClientKeys
      .map((key) => decodeKey(key))
      .filter(({ childId }) => childId !== ALL)
      .map(({ childId }) => childId),
  );
  const allBrandsClients = new Set(
    brandKeys.filter((key) => decodeKey(key).childId === ALL).map((key) => decodeKey(key).parentId),
  );
  const keptBrandKeys = pickers.brands
    ? brandKeys.filter((key) => {
        const { parentId, childId } = decodeKey(key);
        if (!specificClientIds.has(parentId)) return false;
        return childId === ALL || !allBrandsClients.has(parentId);
      })
    : [];

  return { entityIds, clientKeys: keptClientKeys, brandKeys: keptBrandKeys };
}

export function getSelectedClientIds({ clientKeys }: ScopeSelection): string[] {
  return clientKeys
    .map((key) => decodeKey(key))
    .filter(({ childId }) => childId !== ALL)
    .map(({ childId }) => childId);
}

export function rememberItems(
  cache: Record<string, NamedItem>,
  itemsByParent: Record<string, NamedItem[]>,
) {
  Object.entries(itemsByParent).forEach(([parentId, items]) =>
    items.forEach((item) => {
      cache[encodeKey(parentId, item.id)] = item;
    }),
  );
}

export function withSelectedItems(
  itemsByParent: Record<string, NamedItem[]>,
  selectedKeys: string[],
  cache: Record<string, NamedItem>,
): Record<string, NamedItem[]> {
  const merged = Object.fromEntries(
    Object.entries(itemsByParent).map(([parentId, items]) => [parentId, [...items]]),
  );
  selectedKeys.forEach((key) => {
    const { parentId, childId } = decodeKey(key);
    if (childId === ALL || !merged[parentId]) return;
    if (merged[parentId].some((item) => item.id === childId)) return;
    merged[parentId].push(cache[key] ?? { id: childId, label: childId });
  });
  return merged;
}

export function buildClientOptions(
  entities: NamedItem[],
  clientsByEntity: Record<string, NamedItem[]>,
  clientKeys: string[],
): ScopeOption[] {
  const selected = new Set(clientKeys);
  return entities.flatMap((entity) => {
    const allSelected = selected.has(encodeAllKey(entity.id));
    return [
      { value: encodeAllKey(entity.id), label: joinLabel(entity.label, "All clients") },
      ...(clientsByEntity[entity.id] ?? []).map((client) => ({
        value: encodeKey(entity.id, client.id),
        label: joinLabel(entity.label, client.label),
        disabled: allSelected,
      })),
    ];
  });
}

export function buildBrandOptions(
  clients: NamedItem[],
  brandsByClient: Record<string, NamedItem[]>,
  brandKeys: string[],
): ScopeOption[] {
  const selected = new Set(brandKeys);
  return clients.flatMap((client) => {
    const allSelected = selected.has(encodeAllKey(client.id));
    return [
      { value: encodeAllKey(client.id), label: joinLabel(client.label, "All brands") },
      ...(brandsByClient[client.id] ?? []).map((brand) => ({
        value: encodeKey(client.id, brand.id),
        label: joinLabel(client.label, brand.label),
        disabled: allSelected,
      })),
    ];
  });
}

export function deriveScopeSummary(
  selection: ScopeSelection,
  pickers: VisiblePickers,
  labels: ScopeLabels,
): ScopeSummaryRow[] {
  const { entityIds, clientKeys, brandKeys } = pruneSelection(selection, pickers);
  const entityLabel = (id: string) => labels.entities[id] ?? id;
  const clientLabel = (id: string) => labels.clients[id] ?? id;
  const brandLabel = (id: string) => labels.brands[id] ?? id;

  return entityIds.flatMap((tenantId): ScopeSummaryRow[] => {
    const entityKey = encodeAllKey(tenantId);
    const entityOnlyRow: ScopeSummaryRow = {
      key: entityKey,
      label: joinLabel(entityLabel(tenantId), "All clients"),
      target: { tenantId },
    };
    const entityClients = clientKeys
      .map((key) => decodeKey(key))
      .filter(({ parentId }) => parentId === tenantId);
    const specificClients = entityClients.filter(({ childId }) => childId !== ALL);
    if (entityClients.some(({ childId }) => childId === ALL) || specificClients.length === 0) {
      return [entityOnlyRow];
    }

    return specificClients.flatMap(({ childId: clientId }): ScopeSummaryRow[] => {
      const clientTarget = { tenantId, clientId };
      const clientPath = joinLabel(entityLabel(tenantId), clientLabel(clientId));
      const clientBrands = brandKeys
        .map((key) => decodeKey(key))
        .filter(({ parentId }) => parentId === clientId);
      const specificBrands = clientBrands.filter(({ childId }) => childId !== ALL);
      if (clientBrands.some(({ childId }) => childId === ALL)) {
        return [
          {
            key: encodeAllKey(clientId),
            label: joinLabel(clientPath, "All brands"),
            target: clientTarget,
          },
        ];
      }
      if (specificBrands.length === 0) {
        return [{ key: encodeKey(tenantId, clientId), label: clientPath, target: clientTarget }];
      }
      return specificBrands.map(({ childId: brandId }) => ({
        key: encodeKey(clientId, brandId),
        label: joinLabel(clientPath, brandLabel(brandId)),
        target: { ...clientTarget, brandId },
      }));
    });
  });
}
